import { Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EditorMode, FamilySubFilters, GenderFilter, MemberSubFilters, MultimediaFileType, MultimediaSubFilters, MultimediaTypes, NotifierEvents, PopupOpenMode, PopupViewMode, ReferenceType, SearchTabSelection } from 'src/app/common/enums/enums';
import { SearchModel } from 'src/app/common/models/global-search/search.model';
import { GlobalSearchApiServiceService } from 'src/app/services/API/global-search-api.service';
import { IndividualApiService } from 'src/app/services/API/individual-api.service';
import { FamilyApiService } from 'src/app/services/API/family-api.service';
import { dropdownAnim, fadeSlideInOut, descriptionContentAnim } from 'src/app/shared/animations/animations';
import { IndividualFamily } from './Models/IndividualFamily';
import { FamilyModel } from './Models/FamilyModel';
import { TranslateHandler } from 'src/app/common/helpers/translate-handler';
import { SourceDetailsModel } from 'src/app/common/models/global-search/source-details';
import { SourceAPIService } from 'src/app/services/API/source-api.service';
import { MultimediaApiService } from 'src/app/services/API/multimedia-api.service';
import { PlaceAPIService } from 'src/app/services/API/place-api.service';
import { PlaceComponentModel } from '../../common/place/models/place-component-model';
import { MessageDialogService } from '../../common/message-dialog/services/message-dialog.service';
import { environment } from 'src/environments/environment';
import { NotifierV2Service } from 'src/app/services/notifier-v2.service';
import { ProjectRefService } from 'src/app/services/project-ref.service';
import { DialogService } from 'src/app/services/UI/dialog.service';
import { PlaceAddressFormatter } from 'src/app/common/helpers/place/place-address-formatter';
import { EditorReferenceInfo } from 'src/app/common/models/editors/editor-reference-info';
import { Router } from '@angular/router';
import { AppConstatnts } from 'src/app/common/constants/app-contstants';

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  animations: [
    dropdownAnim,
    fadeSlideInOut,
    descriptionContentAnim,
  ]
})
export class GlobalSearchComponent implements OnInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('descPanel') descPanel?: ElementRef<HTMLElement>;
  searchQuery = '';
  expanded = false;
  showDropdown = false;
  searchPrefix = '';
  selectedItem: any = null;
  nextSelectedItem: any = null;
  count: number = 0;
  searchResults: SearchModel[] = [];
  isLoadingResults: boolean = false;
  sourceDetails: SourceDetailsModel = null;
  originalFamilyResults: FamilyModel[] = [];
  familyResults: FamilyModel[] = [];
  locationDetails: PlaceComponentModel = null;
  googleMapUrl: SafeResourceUrl;
  address: any;
  mapUrl: string = '';
  activeEditorMode: EditorMode | null = null;

  // Enums and selection states
  searchTabSelections = SearchTabSelection;
  searchTabSelection: SearchTabSelection = SearchTabSelection.All;
  memberSubFilter: string[] = ['All'];
  familySubFilter: string = 'All';
  multimediaSubFilter: string = SearchTabSelection.All;

  fields: { name: string; value: string; indent?: boolean }[] = [];
  animationState = 'enter';

  private globalClickUnlisten: () => void;
  private searchSubject = new Subject<string>();
  MultimediaFileType = MultimediaFileType;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private globalSearchService: GlobalSearchApiServiceService,
    private individualService:IndividualApiService,
    private familyService: FamilyApiService,
    private translateHandler: TranslateHandler,
    private sourceService: SourceAPIService,
    private multimediaService: MultimediaApiService,
    private placeService: PlaceAPIService,
    private messageDialogService      : MessageDialogService,
    private sanitizer: DomSanitizer,
    protected notifierService: NotifierV2Service,
    private projectRefService: ProjectRefService,
    private dialogService: DialogService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.globalClickUnlisten = this.renderer.listen('document', 'click', (event) => {
      const clickedInside = this.elementRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.onClear();
        if (!this.searchQuery.trim()) {
          this.expanded = false;
        }
      }
    });

    this.getSearchFieldDataAsObservable();
  }

  filteredResults = [...this.searchResults];

  tabOptions = [
    { label: 'lbl_all', value: SearchTabSelection.All },
    { label: 'lbl_member', value: SearchTabSelection.Member },
    { label: 'lbl_family', value: SearchTabSelection.Family },
    { label: 'lbl_location', value: SearchTabSelection.Location },
    { label: 'lbl_sources', value: SearchTabSelection.Source },
    { label: 'lbl_multimedia', value: SearchTabSelection.Multimedia }
  ];

  memberSubFilters = [
    { key: MemberSubFilters.FirstName, label: 'lbl_search_sub_filters.lbl_first_name' },
    { key: MemberSubFilters.LastName, label: 'lbl_search_sub_filters.lbl_last_name' },
    { key: MemberSubFilters.Gender, label: 'lbl_search_sub_filters.lbl_gender' }
  ];

  familySubFilters = [
    { key: FamilySubFilters.All, label: 'lbl_search_family_sub_filters.lbl_all' },
    { key: FamilySubFilters.Male, label: 'lbl_search_family_sub_filters.lbl_male' },
    { key: FamilySubFilters.Female, label: 'lbl_search_family_sub_filters.lbl_female' },
    { key: FamilySubFilters.Unknown, label: 'lbl_search_family_sub_filters.lbl_unknown' },
  ];

  multimediaSubFilters = [
    { key: 'All', label: 'lbl_search_multimedia_sub_filters.lbl_all' },
    { key: 'Image', label: 'lbl_search_multimedia_sub_filters.lbl_image' },
    { key: 'Audio', label: 'lbl_search_multimedia_sub_filters.lbl_audio' },
    { key: 'Video', label: 'lbl_search_multimedia_sub_filters.lbl_video' },
    { key: 'Document', label: 'lbl_search_multimedia_sub_filters.lbl_document' }
  ];

  onSearchChange(value: string) {
  // Remove only leading spaces, keep middle ones
  this.searchQuery = value.replace(/^\s+/, '');
  this.searchSubject.next(this.searchQuery);
}

 preventLeadingSpace(event: KeyboardEvent) {
  if (event.key === ' ' && !this.searchQuery) {
    event.preventDefault(); // block first space
  }
}

  onFocus() {
    this.expanded = true;
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    });
  }

  getSearchFieldDataAsObservable() {
    return this.searchSubject.asObservable()
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe(query => {
        this.searchQuery = query;
        this.filterResults();
        this.showDropdown = !!query.trim();
        if (this.searchQuery.trim() !== '') {
          this.searchAllData(this.searchQuery.trim()); //Only triggers if not empty
        }
      });
  }

  onInputChange() {
    this.searchSubject.next(this.searchQuery); // debounced input
  }

  onClear() {
    this.searchQuery = '';
    this.showDropdown = false;
    this.selectedItem = null;
    this.memberSubFilter = ['firstName'];
    this.familySubFilter = 'All';
    this.multimediaSubFilter = 'All';
    this.searchSubject.next(''); // emit empty to reset distinctUntilChanged()
    this.searchInput.nativeElement.blur();
    setTimeout(() => {
      this.expanded = false;
    }, 200);
  }

  selectTab(tab: SearchTabSelection) {
    this.clearFields();
    this.searchTabSelection = tab;
    this.memberSubFilter = ['firstName']; // Reset member subfilter if switching tabs
    this.familySubFilter = 'All'; // Reset family subfilter if switching tabs
    this.multimediaSubFilter = 'All'; // Reset multimedia subfilter if switching tabs
    this.filterResults();
  }

toggleMemberSubFilter(filter: MemberSubFilters) {
  const defaultKey = MemberSubFilters.FirstName;
  const genderKey = MemberSubFilters.Gender;
  const otherKeys: MemberSubFilters[] = [
    MemberSubFilters.FirstName,
    MemberSubFilters.LastName
    ];

  if (filter === genderKey) {
    this.toggleGenderFilter(genderKey, otherKeys, defaultKey);
  } else if (otherKeys.includes(filter)) {
    this.toggleOtherFilter(filter, genderKey, defaultKey);
  }
  this.sortMemberResults(this.memberSubFilter);
}

  private toggleGenderFilter(genderKey: MemberSubFilters.Gender, otherKeys: MemberSubFilters[], defaultKey: MemberSubFilters.FirstName) {
    if (this.memberSubFilter.includes(genderKey)) {
        // Deselect Gender and keep the other if existed
          this.memberSubFilter = this.memberSubFilter.filter(key => key !== genderKey);        
          if (this.memberSubFilter.length === 0) {
              this.memberSubFilter = [defaultKey];
            }
      } else {
        // Select Gender, keep only one other if present
        const selectedOther = this.memberSubFilter.find(f => otherKeys.includes(f as MemberSubFilters));
        this.memberSubFilter = selectedOther ? [genderKey, selectedOther] : [genderKey];
      }
  }

  private toggleOtherFilter(filter: MemberSubFilters, genderKey: MemberSubFilters.Gender, defaultKey: MemberSubFilters.FirstName) {
    if (this.memberSubFilter.includes(genderKey)) {
      if (this.memberSubFilter.includes(filter)) {
        // Deselect the other filter, keep Gender
        this.memberSubFilter = [genderKey,defaultKey];
      } else {
        // Select Gender + this filter
        this.memberSubFilter = [genderKey, filter];
      }
    } else if (this.memberSubFilter.includes(filter)) {
      // Deselect this filter
      this.memberSubFilter = this.memberSubFilter.filter(f => f !== filter);
      if (this.memberSubFilter.length === 0) {
        this.memberSubFilter = [defaultKey];
      }
    } else {
      // Select only this filter
      this.memberSubFilter = [filter];
    }
  }

  //EM-19570 member sort using firstname and lastname
  sortMemberResults(filter: string[]) {
  const query = this.searchQuery?.toLowerCase() || '';

  const applySorting = (fieldKey: MemberSubFilters) => {
    this.filteredResults = [...this.searchResults].filter(item => item.category === SearchTabSelection.Member).sort((a, b) => {
      const aName = (a.filters?.[fieldKey] || '').toLowerCase();
      const bName = (b.filters?.[fieldKey] || '').toLowerCase();

      const aStarts = aName.startsWith(query);
      const bStarts = bName.startsWith(query);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      return this.customNameSort(a.filters?.[fieldKey] || '', b.filters?.[fieldKey] || '');
    });
  };

  if (filter.includes(MemberSubFilters.LastName)) {
    applySorting(MemberSubFilters.LastName);
  }

  if (filter.includes(MemberSubFilters.FirstName)) {
    applySorting(MemberSubFilters.FirstName);
  }
}

  onFamilySubFilterChange(filter: string,IsTabChange:boolean = false) {
    if(IsTabChange){
      if (this.familySubFilter === filter) {
        this.familySubFilter = FamilySubFilters.All;
      } 
      else {
      this.familySubFilter = filter;
      }
    }
    if ( this.familySubFilter === FamilySubFilters.All) {
      this.familyResults = [...this.originalFamilyResults];
      return;
    }
    const query = this.searchQuery?.toLowerCase() || '';
    const searchParts = query.split(' ').filter(Boolean);
    const genderCode = GenderFilter[this.familySubFilter as keyof typeof GenderFilter]; 

    this.familyResults = this.originalFamilyResults.filter(result => {
        const p1Match = result.partner1 &&
                  result.partner1.gender === genderCode &&
                  searchParts.every(part => result.partner1.name.toLowerCase().includes(part));

        const p2Match = result.partner2 &&
                  result.partner2.gender === genderCode &&
                  searchParts.every(part => result.partner2.name.toLowerCase().includes(part));

      return p1Match || p2Match;
    }).map(result => {
    if (result.partner2 && result.partner2.gender === genderCode && result.partner1.gender!==genderCode) {
      return {
        ...result,
        partner1: result.partner2,
        partner2: result.partner1
      };
    }
    return result;
  });
  this.familyResults = this.familySort(this.familyResults,query);
  }

  onMultimediaSubFilterChange(filter: string,IsTabChange:boolean = false) {
    if(IsTabChange){
      if (this.multimediaSubFilter === filter) {
        this.multimediaSubFilter = MultimediaSubFilters.All;
      } else {
      this.multimediaSubFilter = filter;
      }
    }
    if (this.multimediaSubFilter === SearchTabSelection.All) {
      this.filteredResults = this.searchResults.filter(item => item.category === SearchTabSelection.Multimedia);
      return;
    }
    // Lookup enum value (e.g., "PHOTO" -> 1)
    const multimediaType = MultimediaFileType[filter as keyof typeof MultimediaFileType];

    if (multimediaType === undefined) {
      console.warn('Invalid multimedia filter:', filter);
      this.filteredResults = [];
    }

      const validTypes = [
      MultimediaFileType.Image,
      MultimediaFileType.Audio,
      MultimediaFileType.Video,
      MultimediaFileType.Document
    ];

    if (validTypes.includes(multimediaType)) {
      this.filteredResults = this.searchResults.filter(
        item =>
          item.category === SearchTabSelection.Multimedia &&
          item.filters?.fileType === multimediaType
      );
    } else {
      this.filteredResults = [];
    }

  }

  filterResults() {
  this.isLoadingResults = true;

  if(this.searchTabSelection === SearchTabSelection.Family){
    this.showFamilyResults();
    if(this.familySubFilter !== FamilySubFilters.All){
      this.onFamilySubFilterChange(this.familySubFilter)
    }
  }

  this.filteredResults = this.searchResults.filter(item => {
    if (this.searchTabSelection === SearchTabSelection.All) {
      return item.category !== SearchTabSelection.Family;
    }
    return item.category === this.getCategoryNameFromTab(this.searchTabSelection);
  });

  if(this.searchTabSelection == SearchTabSelection.Member){
    this.sortMemberResults(this.memberSubFilter)
  }

  if(this.searchTabSelection === SearchTabSelection.Multimedia) {
    this.onMultimediaSubFilterChange(this.multimediaSubFilter);
  }
  
  this.isLoadingResults = false;
  }

  getCategoryNameFromTab(tab: SearchTabSelection): string {
    switch (tab) {
      case SearchTabSelection.Member:
        return 'Member';
      case SearchTabSelection.Family:
        return 'Family';
      case SearchTabSelection.Location:
        return 'Location';
      case SearchTabSelection.Source:
        return 'Source';
      case SearchTabSelection.Multimedia:
        return 'Multimedia';
      default:
        return '';
    }
  }
  getGenderClass(gender: string): string {
  switch (gender) {
    case GenderFilter.Male: return 'male';
    case GenderFilter.Female: return 'female';
    case GenderFilter.Unknown: return 'unknown';
    default: return '';
  }
}

  highlightMatch(text: string | undefined | null, query: string): string {
    if (!text) return '';
    if (!query) return text;

    // Split query into individual words, ignoring extra spaces
    const words = query.toLowerCase().split(/\s+/).filter(word => word); // remove empty strings

    if (words.length === 0) return text;

    // Escape special regex characters
    const escapeRegex = (word: string) => word.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

    // Build regex to match any of the words
    const regex = new RegExp(`(${words.map(escapeRegex).join('|')})`, 'gi');

    // Replace matches with <strong>
    return text.replace(regex, (match) => `<strong>${match}</strong>`);
  }

  onItemClick(item: any) {
    // wait one microtask so *ngIf bindings apply
    queueMicrotask(() => this.scrollDescToTop());

    // and once more next frame in case layout/height changed
    requestAnimationFrame(() => this.scrollDescToTop());

    if (this.selectedItem && this.selectedItem.fullName !== item.fullName) {
      // existing item is shown, animate out first
      this.animationState = 'leave';
      this.nextSelectedItem = item;
    } else {
      // first time open
      this.selectedItem = { ...item };
      this.updateFields(this.selectedItem);
      this.animationState = 'enter';
    }
  }

  private scrollDescToTop() {
    const el = this.descPanel?.nativeElement;
    if (!el) return;
    el.scrollTop = 0;
  }

  onAnimationDone() {
    if (this.animationState === 'leave' && this.nextSelectedItem) {
      // swap content after fade out
      this.selectedItem = { ...this.nextSelectedItem };
      this.updateFields(this.selectedItem);
      this.animationState = 'enter';
      this.nextSelectedItem = null;
    }
    this.scrollDescToTop();
  }

  updateFields(item: any) {
    switch (item.category) {
      case SearchTabSelection.Member:
       this.getMemberDetails(item);
        break;

      case SearchTabSelection.Multimedia:
        this.getMultimediaDetails(item);
        break;

      case SearchTabSelection.Family:
        this.getFamilyDetails(item);
        break;

      case SearchTabSelection.Location:
        this.getLocationDetails(item.id);
        break;

      case SearchTabSelection.Source:
        this.getSourceDetails(item.id);
        break;
    }
  }

  ngOnDestroy(): void {
    if (this.globalClickUnlisten) {
      this.globalClickUnlisten();
    }
  }

  searchAllData(searchText: string) {
    this.isLoadingResults = true;
    this.searchResults = [];
    this.filteredResults = [];
    this.familyResults=[];
    this.clearFields();
    this.globalSearchService.searchAllData(searchText)
      .pipe(
        tap()
      ).subscribe({
        next: (res: any) => {
          if (!res.data || res?.data.length === 0) {
            this.isLoadingResults = false;
            this.searchResults = [];
            this.filteredResults = [];
            this.familyResults = [];
            this.count = 0;
            return;
          }
          this.searchResults = res?.data;
          this.count = res?.data;
          this.filterResults(); // Filter results after fetching
        },
        error: (error) => {
          this.isLoadingResults = false;
          this.searchResults = [];
          this.filteredResults = [];
          this.count = 0;
          console.error('Error fetching search results:', error);
        }
      });
  }

  getSourceDetails(sourceId: number) {
    this.sourceService.getSourceDetails(sourceId).subscribe({
      next: (res: any) => {
        // Handle the response from the service
        this.sourceDetails = res.data;
        this.fields = [
          { name: this.translateHandler.translate('lbl_search_sub_filters.lbl_id'), value: String(this.sourceDetails.id || '-') },
          { name: this.translateHandler.translate('lbl_title'), value: this.sourceDetails.title || '-' },
          { name: this.translateHandler.translate('lbl_period'), value: this.sourceDetails.period || '-' },
          { name: this.translateHandler.translate('lbl_author'), value: this.sourceDetails.author || '-' },
          { name: this.translateHandler.translate('lbl_publication'), value: this.sourceDetails.publication || '-' },
          { name: this.translateHandler.translate('lbl_search_description.lbl_repository'), value: this.sourceDetails.repository || '-' },
          { name: this.translateHandler.translate('lbl_search_description.lbl_copy'), value: this.sourceDetails.copy || '-' },
          { name: this.translateHandler.translate('lbl_search_description.lbl_used'), value: this.sourceDetails.used !== undefined && this.sourceDetails.used !== null ? `${this.sourceDetails.used} ${this.translateHandler.translate('lbl_search_description.lbl_time')}`: '-' }
        ];
      },
      error: (error) => {
        console.error('Error fetching source details:', error);
      }
    });
  }
  getLocationDetails(locationId: number) {
    this.placeService.getPlaceById(locationId).subscribe({
      next: (res: any) => {
        this.locationDetails = res.data;
        this.fields = [
          { name: this.translateHandler.translate('lbl_search_location_sub_filters.lbl_type'), value: this.translateHandler.translate(`lbl_search_location_sub_filters.lbl_places.${this.locationDetails.type}`) || '-' },
          { name: this.translateHandler.translate(`lbl_search_location_sub_filters.lbl_view.${this.locationDetails.type}`), value: String(this.locationDetails.detail || '-') },
          { name: this.translateHandler.translate(`lbl_search_location_sub_filters.lbl_place.${this.locationDetails.type}`), value: String(this.locationDetails.line2 || '-') },
          { name: this.translateHandler.translate(`lbl_search_location_sub_filters.lbl_by.${this.locationDetails.type}`), value: String(this.locationDetails.line3 || '-') },
          { name: this.translateHandler.translate(`lbl_search_location_sub_filters.lbl_municipality.${this.locationDetails.type}`), value: String(this.locationDetails.line4 || '-') },
          { name: this.translateHandler.translate(`lbl_search_location_sub_filters.lbl_region.${this.locationDetails.type}`), value: String(this.locationDetails.line5 || '-') },
          { name: this.translateHandler.translate('lbl_search_location_sub_filters.lbl_land'), value: String(this.locationDetails.country || '-') },
          { name: this.translateHandler.translate('lbl_search_location_sub_filters.lbl_latitude'), value: String(this.locationDetails.lat || '-') },
          { name: this.translateHandler.translate('lbl_search_location_sub_filters.lbl_longitude'), value: String(this.locationDetails.lng || '-') },
          { name: this.translateHandler.translate('lbl_search_description.lbl_used'), value: this.locationDetails.used !== undefined && this.locationDetails.used !== null ? `${this.locationDetails.used} ${this.translateHandler.translate('lbl_search_description.lbl_time')}`: '-' }
        ];
      },
      error: (error) => {
        console.error('Error fetching location details:', error);
      }
    });
  }

  getMemberDetails(item: any) {
    this.individualService.getMemberDetails<{data:IndividualFamily}>(item.id).subscribe({
      next: (res: any) => {
        // Handle the response from the service
       const member = res.data;  
          this.fields = [
            { name: this.translateHandler.translate('lbl_search_description.lbl_gender'),value: this.translateHandler.translate(member.gender === 'M' ? 'lbl_male' : member.gender === 'F' ? 'lbl_female' :'lbl_unknown')},
            { name: this.translateHandler.translate('lbl_live_status'), value: this.translateHandler.translate(member.aliveStatus === 0 ? 'lbl_unknown' : member.aliveStatus === 1 ? 'lbl_living' : member.aliveStatus === 2 ? 'lbl_deceased' : '-') },
            { name: this.translateHandler.translate('lbl_birthday'), value: member.birthDate || '-' },
            { name: this.translateHandler.translate('lbl_death_date'), value: member.deathDate || '-' },
            { name: this.translateHandler.translate('lbl_fathers_name'), value: member.father || '-' },
            { name: this.translateHandler.translate('lbl_mothers_name'), value: member.mother || '-' },
            { name: this.translateHandler.translate('lbl_spouses_name'), value: member.spouse || '-' },
            ...(member.children ?? []).map((child: string, i: number) => ({
              name: this.translateHandler.translate('lbl_childindex', [(i + 1).toString()]),
              value: child,
              indent: true
            }))
          ];
  
          item.avatarUrl = member.profilePictureURL?.trim()
                          ? member.profilePictureURL
                          : member.gender === 'F'
                            ? 'assets/images/defaults/default-member-woman.svg'
                            : member.gender === 'M'
                              ? 'assets/images/defaults/default-member-man.svg'
                              : 'assets/images/defaults/default-member-unknown-gender.svg';

          item.avatarStyle = 'member';
      },
      error: (error) => {
        console.error('Error fetching member details:', error);
      }
    });
  }
  getFamilyDetails(item: any) {
    this.familyService.getFamilyDetails(item.familyId).subscribe({
      next: (res: any) => {
       const family = res.data;  
          this.fields = [
            { name: this.translateHandler.translate('lbl_search_family_sub_filters.lbl_marriage_date'), value: family.marriageDate || '-' },
            { name: this.translateHandler.translate('lbl_search_family_sub_filters.lbl_marriage_place'), value: family.marriagePlace || '-' },
            { name: this.translateHandler.translate('lbl_search_family_sub_filters.lbl_children'), value: family.childrenCount !== undefined && family.childrenCount !== null ? `${family.childrenCount} ${this.translateHandler.translate('lbl_search_family_sub_filters.lbl_children')}`: '-' },
          ];
  
          item.avatarUrl = family.familyImage?.trim() ? family.familyImage : '/assets/images/defaults/default-family.svg';
          item.avatarStyle = 'family';
      },
      error: (error) => {
        console.error('Error fetching family details:', error);
      }
    });
  }

  getMultimediaDetails(item: any) {
    // Placeholder for multimedia details logic
    this.multimediaService.getMultimediaById(item.id).subscribe({
      next: (res: any) => {
        // Handle the response from the service
        const multimedia = res.data;
        this.fields = [
          { name: this.translateHandler.translate('lbl_search_description.lbl_image_description'), value: multimedia?.caption || '-' },
          { name: this.translateHandler.translate('lbl_search_description.lbl_flagg'), value: multimedia?.tag || '-' },
          { name: this.translateHandler.translate('lbl_search_description.lbl_given'), value: multimedia.date?.genealogyDate?.originalStr || '-' },
          { name: this.translateHandler.translate('lbl_search_description.lbl_place_name'), value: multimedia?.place || '-' },
          { name: this.translateHandler.translate('lbl_description'), value: multimedia?.description || '-' },
        ];
        //item.avatarUrl = 'https://static.vecteezy.com/system/resources/previews/029/271/069/non_2x/avatar-profile-icon-in-flat-style-female-user-profile-illustration-on-isolated-background-women-profile-sign-business-concept-vector.jpg';
        switch (multimedia.mediaType) {
          case MultimediaFileType.Image:
            item.avatarUrl = multimedia.mediaUrl ? multimedia.mediaUrl : '/assets/images/defaults/default-image.svg';
            break;
          case MultimediaFileType.Audio:
            item.avatarUrl = '/assets/images/defaults/default-audio.svg';
            break;
          case MultimediaFileType.Video:
            item.avatarUrl = '/assets/images/defaults/default-video.svg';
            break;
          case MultimediaFileType.Document:
            item.avatarUrl = '/assets/images/defaults/default-document.svg';
            break;
            
          default:
            item.avatarUrl = '/assets/images/defaults/default-cracked-file.svg';
        }
        item.avatarStyle = 'multimedia';
      },
      error: (error) => {
        console.error('Error fetching multimedia details:', error);
      }
    });

  }

  clearFields() {
  this.selectedItem = null;
  }

  //EM-19570 custom sort to sort alphabet, then special character , then numbers
  customNameSort(a: string, b: string): number {
  const getCategory = (name: string): number => {
    const firstChar = name.charAt(0);
    if (/^[A-Za-zÆØÅæøå]/.test(firstChar)) return 0; // letters (including Nordic)
    if (/^\d/.test(firstChar)) return 1;             // numbers
    return 2;                                         // symbols/others
  };

  const categoryA = getCategory(a);
  const categoryB = getCategory(b);

  if (categoryA !== categoryB) {
    return categoryA - categoryB;
  }

  if (categoryA === 1) {
    return parseInt(a) - parseInt(b);
  }

  return a.localeCompare(b, 'no', { sensitivity: 'base' });
}

showFamilyResults(){
  this.originalFamilyResults =[];
  const query = this.searchQuery?.toLowerCase() || '';
  const searchParts = query.split(' ').filter(Boolean);

  this.searchResults
    .filter(item => item.category === SearchTabSelection.Family)
    .forEach((item, index) => {
      const { husband, wife } = item.filters || {};
      const base = { familyId: item.id, category: item.category };
      
      if (husband && wife) {
        const husbandLower = husband.name.toLowerCase();
        const wifeLower = wife.name.toLowerCase();

        const husbandMatchesAll = searchParts.every(term => husbandLower.includes(term));
        const wifeMatchesAll = searchParts.every(term => wifeLower.includes(term));

        if (husbandMatchesAll && !wifeMatchesAll) {
          this.originalFamilyResults.push({
            ...base,
            partner1: husband,
            partner2: wife,
          });
        } else if (!husbandMatchesAll && wifeMatchesAll) {
          this.originalFamilyResults.push({
            ...base,
            partner1: wife,
            partner2: husband,
          });
        } else {
          // Both matched all terms or neither matched all terms - add both ways
          this.originalFamilyResults.push({
            ...base,
            partner1: husband,
            partner2: wife,
          });
          this.originalFamilyResults.push({
            ...base,
            partner1: wife,
            partner2: husband,
          });
        }

      }
      else{
          const partner = wife ?? husband;
          this.originalFamilyResults.push({
            ...base,
            partner1: partner,
            partner2: {name: "-"},
          });
      }
    });
  this.originalFamilyResults = this.familySort(this.originalFamilyResults,query);
  const seen = new Set<number>();
  this.originalFamilyResults = this.originalFamilyResults.filter(item => {
  if (seen.has(item.familyId)) {
    return false;
  }
  seen.add(item.familyId);
  return true;
});
this.familyResults = [...this.originalFamilyResults];

}

// Opens Google Map with the specified location detail in a new browser tab.
  async openGoogleMap(item): Promise<void> {

    let apiKey = environment.GEOCODE.googleAccessToken;
    let title = this.translateHandler.translate("place.geocode.lbl_moving_to_google_map");
    let getLocationMapInfo = this.translateHandler.translate("place.geocode.lbl_moving_to_get_geocodes");
    let checkLocationMapInfo  = this.translateHandler.translate("place.geocode.lbl_moving_to_check_location");

    event.stopPropagation(); // prevent triggering onItemClick
    try {
      const res: any = await this.placeService.getPlaceById(item.id).toPromise();
      this.locationDetails = res.data;

      // Build Google Maps query
      if (this.locationDetails.lat != 0 && this.locationDetails.lng != 0) {
        // Use coordinates if available
        this.address = `${this.locationDetails.lat},${this.locationDetails.lng}`;
      } else {
        // Build an approximate address string
        const addressParts = [
          this.locationDetails.detail,
          this.locationDetails.line2,
          this.locationDetails.line3,
          this.locationDetails.line4,
          this.locationDetails.line5,
          this.locationDetails.country
        ].filter(Boolean); // remove null/undefined/empty
        this.address = encodeURIComponent(addressParts.join(', '));
      }

      if (item.filters.isLocationAvailable) {
        this.mapUrl = `/assets/map/googleMap.html?lat=${item.filters.latitude}&lng=${item.filters.longitude}&key=${apiKey}&mapType=${("checkLocation")}`;
        this.messageDialogService.openMessage(title, checkLocationMapInfo);

      } else {
        this.mapUrl = `/assets/map/googleMap.html?address=${encodeURIComponent(this.address)}&key=${apiKey}&addressType=${encodeURIComponent(this.locationDetails.type)}`;
        this.messageDialogService.openMessage(title, getLocationMapInfo);
      }

      setTimeout(() => {
        this.messageDialogService.close();
        this.googleMapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.mapUrl);
        window.open(this.mapUrl, '_blank');
      }, environment.PLACE.openMapDelayTimeOut); // Introduce a slight delay before opening the map
    }
    catch (error) {
      console.error('Error fetching location details:', error);
    }

  }

  onDoubleClick(item: any) {
    const currentUrl = this.router.url;
    switch (item.category) {
      case SearchTabSelection.Member:
        if (currentUrl.includes(AppConstatnts.routerModes.TreeEditor)) {
          this.openTreeEditor(item.id);
        }
        else if(currentUrl.includes(AppConstatnts.routerModes.FamilyEditor)) {
          this.updateFamilyEditor(item); 
        }
        else {
          this.openMemberEditor(item); 
        }
        break;
      case SearchTabSelection.Family:
        if (currentUrl.includes(AppConstatnts.routerModes.TreeEditor)) {
          this.openTreeEditor(item.partner1?.id);
        }
        else if(currentUrl.includes(AppConstatnts.routerModes.IndividualEditor)) {
          this.updateMemberEditor(item); 
        } 
        else {
          this.openFamilyEditor(item); 
        }
        break;
      case SearchTabSelection.Source:
        this.openResourceEditor(EditorMode.SourceEditor, item);
        break;
      case SearchTabSelection.Location:
        this.openResourceEditor(EditorMode.PlaceEditorPage, item);
        break;
      default:
        this.openPopup(item);
        break;
    }
  }

  openMemberEditor(item: any) {
    this.openUpdateMemberEditor(item.id);
  }

  updateMemberEditor(item: any) {
    this.openUpdateMemberEditor(item.partner1?.id);
  }

  private openUpdateMemberEditor(id: number) {
    let data = {
      id: id,
      editorMode: EditorMode.IndividualEditor,
      isUpdateHistory: true
    }

    this.individualService.getIndividualById(id).subscribe({
      next: (res: any) => {
        let rootMember = {
          DisplayName: res.data?.displayName,
          FirstName: '',
          LastName: '',
          Id: res.data?.id,
          defaultFamilyId: '',
          husbandDisplayName: null,
          wifeDisplayName: null
        }
        this.notifierService.updateEvent(NotifierEvents.RootMemberChanged, rootMember);
      },
      error: (error) => {
        console.error('Error fetching member details:', error);
      }
    });
    this.onClear();
    this.notifierService.setCurrentEditor(`/editor/individualeditor`, data, null);
  }

  openFamilyEditor(item: any) {
    this.individualService.getIndividualById(item.partner1?.id).subscribe({
      next: (res: any) => {
        let rootMember = {
          DisplayName: res.data?.displayName,
          FirstName: '',
          LastName: '',
          Id: res.data?.id,
          defaultFamilyId: '',
          husbandDisplayName: null,
          wifeDisplayName: null
        }
        this.notifierService.updateEvent(NotifierEvents.RootMemberChanged, rootMember);
      },
      error: (error) => {
        console.error('Error fetching member details:', error);
      }
    });

    let data = {
      id: item.familyId,
      editorMode: EditorMode.FamilyEditor,
      individualId: item.partner1?.id || item.partner2?.id || 0,
      family: {},
      isUpdateHistory: true
    }

    this.onClear();
    this.notifierService.updateEvent(NotifierEvents.RootFamilyChanged, data);
    this.notifierService.setCurrentEditor("/editor/familyeditor", data, null);
  }

  updateFamilyEditor(item: any) {
    this.individualService.getIndividualById(item.id).subscribe({
      next: (res: any) => {
        let rootMember = {
          DisplayName: res.data?.displayName,
          FirstName: '',
          LastName: '',
          Id: res.data?.id,
          defaultFamilyId: '',
          husbandDisplayName: null,
          wifeDisplayName: null
        }
        this.notifierService.updateEvent(NotifierEvents.RootMemberChanged, rootMember);
      },
      error: (error) => {
        console.error('Error fetching member details:', error);
      }
    });

    this.familyService.getDefaultFamilyByIndividualId(item.id).subscribe({
      next: (res: any) => {
        const data = {
          id: res?.data.id || 0,
          editorMode: EditorMode.FamilyEditor,
          individualId: item.id || 0,
          family: {},
          isUpdateHistory: true
        };
        this.onClear();
        this.notifierService.updateEvent(NotifierEvents.RootFamilyChanged, data);
        this.notifierService.setCurrentEditor("/editor/familyeditor", data, null);
      },
      error: (error) => {
        console.error('Error fetching family details:', error);
      }
    });
  }

  openTreeEditor(rootmemberId: number) {
    this.individualService.getIndividualById(rootmemberId).subscribe({
      next: (res: any) => {
        let rootMember = {
          DisplayName: res.data?.displayName,
          FirstName: '',
          LastName: '',
          Id: res.data?.id,
          defaultFamilyId: '',
          husbandDisplayName: null,
          wifeDisplayName: null
        }
        this.notifierService.updateEvent(NotifierEvents.RootMemberChanged, rootMember);
      },
      error: (error) => {
        console.error('Error fetching member details:', error);
      }
    });
    this.onClear();
  }

  openResourceEditor(editorMode: EditorMode, item: any) {
    this.activeEditorMode = editorMode;
    let ref: EditorReferenceInfo = {
      id: item.id,
      isBreadcrumbUpdate: true,
    }
    this.notifierService.openEditor(editorMode, ref);
    this.onClear();
  }


  familySort(familyResults: FamilyModel[], query: string): FamilyModel[] {
    const searchParts = query.split(' ').filter(Boolean);
    const firstPart = searchParts[0]?.toLowerCase() || '';

    familyResults.sort((a, b) => {
    const aName = a.partner1?.name.toLowerCase() || '';
    const bName = b.partner1?.name.toLowerCase() || '';

    const aStarts = aName.startsWith(query);
    const bStarts = bName.startsWith(query);

    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;

    // to prioritize the first part of query match second
    const aFirstPartStarts = aName.startsWith(firstPart);
    const bFirstPartStarts = bName.startsWith(firstPart);

    if (aFirstPartStarts && !bFirstPartStarts) return -1;
    if (!aFirstPartStarts && bFirstPartStarts) return 1;

    return this.customNameSort(a.partner1.name || '', b.partner1.name || '');
    });

    return familyResults;
  }
    
  async openPopup(item:any) {
        let projectName = this.projectRefService.getCurrentProject().projectName;
        let name = item.category == SearchTabSelection.Location? "Place":item.category;
        let mainTitle = this.titleMap.get(name);
        let rawData = "";
        if(item.category === SearchTabSelection.Location){
        const res: any = await this.placeService.getPlaceById(item.id).toPromise();
        this.locationDetails = res.data;
        rawData = PlaceAddressFormatter.getFullAddress(this.locationDetails) as string
        }
        var dialogSubscription = this.dialogService.setFull().open(name, {
          id: 0,
          reference: {
            Id: item.id ?? 0,
            type: ReferenceType.None,
            rawData : rawData ?? ""
          },
          title       : this.translateHandler.translate('lbl_menu_list_title', [this.translateHandler.translate(mainTitle), projectName]) ?? "",
          mediaType   : [MultimediaTypes.PHOTO, MultimediaTypes.DOC, MultimediaTypes.AUDIO, MultimediaTypes.VIDEO],
          viewMode    : PopupViewMode.EditorView, // Gallery or editor view
          editMediaId : item.id ?? 0, // MediaId which supposes to be edited
          editSourceId: item.id ?? 0,
          isStandAlone: true,
          mode        : PopupOpenMode.Popup, // Todo: Make a enum
          showMode    : [1],
          isSearchNavigation:true
        }
        )
   }

     titleMap = new Map<string, string>(
    [
      ["Multimedia", "lbl_media_list"], 
      ["Place",      "lbl_place_list"],
      ["Source",     "lbl_source_list"]
    ]
  );
}



